import { memo, useState, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkCjkFriendly from "remark-cjk-friendly";
import { Check, Copy } from "lucide-react";
import { copyText } from "../../ui/clipboard";
import "./markdown.css";

/**
 * Markdown 渲染（对应 iOS 的 MarkdownView）：标题、列表、引用、代码块、表格、分隔线 + 行内样式。
 * 不渲染任何原始 HTML（回复内容来自模型，不能让它往页面里插脚本）。
 * remark-cjk-friendly 修正中文常见的“**标题：**正文”不被识别成粗体的问题（iOS 里也专门处理过）。
 */
export const Markdown = memo(function Markdown({ text, small }: { text: string; small?: boolean }) {
  return (
    <div className={`md${small ? " md-small" : ""}`}>
      <ReactMarkdown remarkPlugins={plugins} components={components} skipHtml>
        {text}
      </ReactMarkdown>
    </div>
  );
});

const plugins = [remarkGfm, remarkCjkFriendly];

const components: Components = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const text = String(children ?? "");
    const lang = /language-(\S+)/.exec(className ?? "")?.[1];
    // 行内代码：没有语言、只有一行
    if (!lang && !text.includes("\n")) return <code className="md-inline-code">{children}</code>;
    return <CodeBlock language={lang ?? ""} code={text.replace(/\n$/, "")} />;
  },
  table: ({ children }) => (
    <div className="md-table"><table>{children}</table></div>
  ),
  input: ({ checked }) => <span className={`md-check${checked ? " on" : ""}`}>{checked ? "✓" : ""}</span>,
  img: ({ src, alt }) => (typeof src === "string" ? <img src={src} alt={alt ?? ""} className="md-img" loading="lazy" /> : null),
};

function CodeBlock({ language, code }: { language: string; code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="md-code">
      <div className="md-code-head">
        <span className="mono t-caption t-2">{language || "code"}</span>
        <button className="t-caption t-2" onClick={() => { void copyText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "已复制" : "复制"}
        </button>
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );
}

/** 只要文字（复制用）：去掉 Markdown 符号 */
export function markdownToPlain(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, (m) => m.replace(/```\w*\n?/g, ""))
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/(^|[^*])\*(?!\s)(.+?)\*/g, "$1$2")
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^>\s?/gm, "")
    .trim();
}

export function Paragraphs({ children }: { children: ReactNode }) {
  return <div className="md">{children}</div>;
}
